// 백엔드 utils/student_name_policy.py 와 같은 기준으로 자리표시 이름을 판정한다.
// '학생 3', '답안지', 파일명 같은 값은 이름이 아니므로 서버에 보내지 않는다.

export const NAME_SOURCE = Object.freeze({
  TEACHER: 'teacher',
  AI_PREVIEW: 'ai_preview',
  FILENAME: 'filename',
  PLACEHOLDER: 'placeholder',
});

export const placeholderStudentLabel = (index) => `학생 ${index + 1}`;

const GENERIC_TOKENS = [
  '답안지', '답안', '답지', '시험지', '시험', '학습지', '과제물', '과제', '보고서', '리포트',
  '수행평가', '수행', '평가지', '평가', '활동지', '활동', '서술형', '서술', '논술형', '논술',
  '제출물', '제출', '스캔본', '스캔', '사본', '복사본', '문서', '파일', '이미지', '사진',
  '첨부파일', '첨부', '학생답안', '학생', '제목없음', '무제', '새문서', '최종본', '최종',
  '수정본', '원본', '양식', '서식', '워크시트', '탐구', '실험', '독서', '감상문', '소감문',
  '글쓰기', '에세이', '발표', '자료', '프로젝트', '포트폴리오', '학년', '학기', '차시', '단원',
  '모둠', '회차', '주차', '국어', '영어', '수학', '사회', '과학', '역사', '도덕', '음악',
  '미술', '체육', '기술', '기가', '정보', '한문', '통합', '통합사회', '통합과학', '물리',
  '화학', '생명', '생물', '지구과학', '지리', '윤리', '경제', '정치', '한국사', '세계사',
  '문학', '화법', '작문', '언어', '매체', '일본어', '중국어', '진로', '창체', '동아리',
  '중간', '기말', '고사', '정답', '채점', '기준', '기준표', '루브릭', '문항', '문제',
  '문제지', '답변', '질문', '결과', '결과지', '기록', '기록지', '작품', '작성', '학습',
  '수업', '복습', '예습', '숙제', '정리', '요약', '개요', '계획서', '계획',
  '영작', '독후감', '일기', '편지', '신문', '보고', '조사', '탐구보고서', '실험보고서',
  '학생부', '생기부', '세특', '교과', '확인', '점검', '제출용', '완성', '완성본', '초안',
  '스캔파일', '카톡', '캡처', '스크린샷', '이미지파일', '사진파일',
].sort((a, b) => b.length - a.length);

const GENERIC_SET = new Set(GENERIC_TOKENS);

const UNKNOWN_MARKERS = new Set([
  'unknown', 'n/a', 'na', 'none', 'null', 'undefined', 'student', 'name',
  '미상', '이름미상', '이름없음', '없음', '미기재', '미입력', '익명', '무명', '판독불가', '확인불가',
  '학생명', '이름', '성명', '학생이름',
]);

const NUMBERED_PLACEHOLDER_RE = /^(?:(?:학생|student|std|이름|미상)\s*[-_#.]?\s*\d{1,4}\s*(?:번)?|\d{1,4}\s*번?\s*(?:학생|student))$/i;

const stripExtension = (value) => String(value || '').replace(/\.[^/.]+$/, '');

const decomposesIntoGeneric = (text) => {
  if (!text) return true;
  return GENERIC_TOKENS.some(token => text.startsWith(token) && decomposesIntoGeneric(text.slice(token.length)));
};

export const normalizeStudentNameInput = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

export const isPlaceholderStudentName = (name, filename = '') => {
  const text = normalizeStudentNameInput(name);
  if (!text) return true;
  const lowered = text.toLowerCase();
  if (UNKNOWN_MARKERS.has(lowered)) return true;
  if (NUMBERED_PLACEHOLDER_RE.test(text)) return true;
  if (filename) {
    const file = normalizeStudentNameInput(filename);
    if (text === file || text === normalizeStudentNameInput(stripExtension(file))) return true;
  }
  const compact = text.replace(/[\s\-_#.()[\]0-9]/g, '');
  if (!compact) return false;
  if (/^[가-힣]+$/.test(compact) && decomposesIntoGeneric(compact)) return true;
  return false;
};

const trimGenericEdges = (run) => {
  let text = run;
  let changed = true;
  while (changed && text.length > 4) {
    changed = false;
    for (const token of GENERIC_TOKENS) {
      if (text.length > token.length && text.startsWith(token)) {
        text = text.slice(token.length);
        changed = true;
        break;
      }
      if (text.length > token.length && text.endsWith(token)) {
        text = text.slice(0, text.length - token.length);
        changed = true;
        break;
      }
    }
  }
  return text;
};

const extractIdLabel = (base) => {
  const triple = base.match(/(?:^|[^\d])(\d{1,2})\s*[-_.]\s*(\d{1,2})\s*[-_.]\s*(\d{1,3})(?![\d])/);
  if (triple) return `${triple[1]}-${triple[2]}-${triple[3]}`;
  const numbered = base.match(/(?:^|[^\d])(\d{1,3})\s*번/);
  if (numbered) return `${numbered[1]}번`;
  return '';
};

// 파일명에서 실명(한글 2~4자)만 꺼낸다. '답안지', '수행평가' 같은 일반 단어는 이름으로 쓰지 않는다.
// 실명이 없고 '2-3-12', '12번' 같은 번호만 있으면 번호를 돌려준다.
const ID_UNIT_RE = /\d+\s*(?:학년|학기|반|번|조|모둠|차시|회차|차|회|주차|단원|쪽|페이지)/g;

export const extractStudentNameFromFilename = (filename) => {
  const base = stripExtension(filename);
  const runs = base.replace(ID_UNIT_RE, ' ').match(/[가-힣]{2,}/g) || [];
  for (const run of runs) {
    if (GENERIC_SET.has(run)) continue;
    const candidate = trimGenericEdges(run);
    if (candidate.length < 2 || candidate.length > 4) continue;
    if (decomposesIntoGeneric(candidate)) continue;
    if (isPlaceholderStudentName(candidate)) continue;
    return candidate;
  }
  return extractIdLabel(base);
};

export const studentNameForSubmit = (name, filename = '') => (
  isPlaceholderStudentName(name, filename) ? '' : normalizeStudentNameInput(name)
);

export const studentNameSourceForSubmit = (name, source, filename = '') => {
  if (isPlaceholderStudentName(name, filename)) return NAME_SOURCE.PLACEHOLDER;
  return source || NAME_SOURCE.AI_PREVIEW;
};

export const nameSourceForTeacherEdit = (value) => (
  normalizeStudentNameInput(value) ? NAME_SOURCE.TEACHER : NAME_SOURCE.PLACEHOLDER
);
